import * as Api from '@ViewModels';
import {
	useAddBehaviorCaseMutation,
	useAddBehaviorSwitchStepMutation,
	useAutomationTemplateRemoveStepMutation,
	useAvailableBehaviorCasesQuery,
	useRemoveBehaviorCaseMutation,
	useUpdateBehaviorCaseMutation,
	useUpdateBehaviorSwitchStepMutation,
} from '../../../../queries/Automation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEdges, useNodes, useReactFlow, type Edge, type Node } from '@xyflow/react';
import { AUTOMATION_QUERY_KEYS } from '../../../../queries/Automation/queryKeys';
import type {
	AvailableBehaviorCase,
	BehaviorCaseStatementPayload,
	BehaviorSwitchStepPayload,
} from '../../../../queries/Automation/mutations/behaviorSwitch/types';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';
import type { BaseNodeData } from '../types';
import type { ParentCaseInfo } from '../types/stepNodes';
import { isAutomationStepNodeData } from '../types/typeGuards';
import { MAX_BRANCH_NESTING_DEPTH } from '../utils/nestingLimits';
import {
	type BehaviorCaseLike,
	type BehaviorGraphState,
	type BehaviorSwitchLike,
	applyOptimisticBehaviorGraphUpdate,
	buildPersistedBehaviorCaseAssignments,
	findBehaviorSwitchForSourceStep,
	isBehaviorSwitchModel,
	isClickBehaviorCase,
	isOpenBehaviorCase,
	isReplyBehaviorCase,
	isTimeoutBehaviorCase,
	syncOptimisticBehaviorEdges,
	syncOptimisticBehaviorNodes,
} from '../utils/optimisticBehaviorSwitchSync';

interface UseBehaviorConditionalOptionsParams {
	nodeId: string;
	step: Api.IAutomationStep | null;
	template?: Api.IAutomationTemplate;
	readonly: boolean;
	parentCaseInfo?: ParentCaseInfo;
	impersonationContext?: Api.IImpersonationContext;
}

interface UseBehaviorConditionalOptionsResult {
	canShowReplyOption: boolean;
	canShowOpenOption: boolean;
	canShowClickOption: boolean;
	takeActionOnReply: boolean;
	takeActionOnOpen: boolean;
	takeActionOnClick: boolean;
	waitTimeoutDays: number;
	showTimeoutEditor: boolean;
	isDisabled: boolean;
	isUpdating: boolean;
	disabledReason: string | null;
	feedbackMessage: string | null;
	timeoutCaseLabel: string;
	onToggleReply: (checked: boolean) => void;
	onToggleOpen: (checked: boolean) => void;
	onToggleClick: (checked: boolean) => void;
	onChangeTimeoutDays: (nextValue: number) => void;
}

const DEFAULT_WAIT_TIMEOUT_DAYS = 3;
const BEHAVIOR_SYNC_DEBOUNCE_MS = 180;

function isEmailLikeStep(step: Api.IAutomationStep | null): boolean {
	return !!step && (step._type === 'EmailAutomationStep' || step._type === 'HtmlNewsletterAutomationStep');
}

const TIMEOUT_CASE_LABEL = 'If no engagement (timeout)';

function formatTimeSpanDays(days: number): string {
	return `${days}.00:00:00`;
}

function parseTimeSpanDays(value: unknown): number {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.max(1, Math.round(value));
	}

	if (typeof value !== 'string') {
		return DEFAULT_WAIT_TIMEOUT_DAYS;
	}

	const trimmed = value.trim();
	if (!trimmed) {
		return DEFAULT_WAIT_TIMEOUT_DAYS;
	}

	const dayMatch = trimmed.match(/^(\d+)\./);
	if (dayMatch) {
		return Math.max(1, Number(dayMatch[1]));
	}

	const parts = trimmed.split(':');
	if (parts.length === 3) {
		const hours = Number(parts[0]) || 0;
		const minutes = Number(parts[1]) || 0;
		const seconds = Number(parts[2]) || 0;
		const totalHours = hours + minutes / 60 + seconds / 3600;
		return Math.max(1, Math.ceil(totalHours / 24));
	}

	return DEFAULT_WAIT_TIMEOUT_DAYS;
}

function hasUsableSourceStepId(sourceStepId: unknown): sourceStepId is string {
	return typeof sourceStepId === 'string' && sourceStepId.trim().length > 0;
}

function doesBehaviorStateMatchSwitch(
	nextState: BehaviorGraphState,
	behaviorSwitch: BehaviorSwitchLike | null,
	sourceStepId?: string
): boolean {
	const cases = behaviorSwitch?.cases || [];
	const hasReplyCase = cases.some(caseStatement => isReplyBehaviorCase(caseStatement));
	const hasOpenCase = cases.some(caseStatement => isOpenBehaviorCase(caseStatement));
	const hasClickCase = cases.some(caseStatement => isClickBehaviorCase(caseStatement));

	if (
		nextState.nextReply !== hasReplyCase ||
		nextState.nextOpen !== hasOpenCase ||
		nextState.nextClick !== hasClickCase
	) {
		return false;
	}

	if (!nextState.nextReply && !nextState.nextOpen && !nextState.nextClick) {
		return !behaviorSwitch?.id;
	}

	if (sourceStepId && !hasUsableSourceStepId(behaviorSwitch?.sourceStepId)) {
		return false;
	}

	return nextState.nextTimeoutDays === parseTimeSpanDays(behaviorSwitch?.waitTimeout);
}

function buildBehaviorSwitchStepUpdatePayload({
	behaviorSwitch,
	sourceStepId,
	waitTimeout,
}: {
	behaviorSwitch: BehaviorSwitchLike;
	sourceStepId: string;
	waitTimeout: string;
}): Partial<BehaviorSwitchStepPayload> {
	const { cases: _cases, ...currentSwitchStep } = behaviorSwitch as Partial<BehaviorSwitchStepPayload>;

	// Case edits are handled by dedicated endpoints. The BehaviorSwitch PUT
	// endpoint preserves existing cases server-side, but replaces the rest of the
	// step model, so keep the current switch fields and always resend sourceStepId.
	return {
		...currentSwitchStep,
		_type: 'BehaviorSwitchAutomationStep',
		id: behaviorSwitch.id,
		name:
			typeof currentSwitchStep.name === 'string' && currentSwitchStep.name.trim().length > 0
				? currentSwitchStep.name
				: 'Conditional Behavior',
		sourceStepId: hasUsableSourceStepId(behaviorSwitch.sourceStepId) ? behaviorSwitch.sourceStepId : sourceStepId,
		waitTimeout,
	};
}

function getTemplateSteps(template?: Api.IAutomationTemplate): Api.IAutomationStep[] {
	return template?.draft?.steps || template?.published?.steps || [];
}

function getBranchNestingDepth(parentCaseInfo?: ParentCaseInfo): number {
	const segmentPathKey = parentCaseInfo?.segmentPathKey;
	if (typeof segmentPathKey === 'string' && segmentPathKey.length > 0) {
		return (segmentPathKey.match(/(?:^|>)(?:segment|behaviorCase):/g) || []).length;
	}

	return parentCaseInfo ? 1 : 0;
}

function getSourceStepName(
	step: Api.IAutomationStep | null,
	nodes: Node<BaseNodeData>[],
	sourceNodeId: string
): string | undefined {
	if (step && 'name' in step && typeof step.name === 'string' && step.name.trim().length > 0) {
		return step.name.trim();
	}

	const sourceNode = nodes.find(node => node.id === sourceNodeId);
	const sourceNodeData = sourceNode?.data as { stepData?: { name?: unknown }; label?: unknown } | undefined;
	if (typeof sourceNodeData?.stepData?.name === 'string' && sourceNodeData.stepData.name.trim().length > 0) {
		return sourceNodeData.stepData.name.trim();
	}
	if (typeof sourceNodeData?.label === 'string' && sourceNodeData.label.trim().length > 0) {
		return sourceNodeData.label.trim();
	}
	return undefined;
}

function isBehaviorSwitchNodeLinkedToSourceStep({
	node,
	edges,
	sourceNodeId,
	sourceStepId,
}: {
	node: Node<BaseNodeData>;
	edges: Edge[];
	sourceNodeId: string;
	sourceStepId: string;
}): boolean {
	const automationStep = (node.data as { automationStep?: BehaviorSwitchLike } | undefined)?.automationStep;
	if (!automationStep || !isBehaviorSwitchModel(automationStep)) {
		return false;
	}

	if (automationStep.sourceStepId === sourceStepId) {
		return true;
	}

	if (hasUsableSourceStepId(automationStep.sourceStepId)) {
		return false;
	}

	return edges.some(edge => {
		if (edge.source !== sourceNodeId || edge.type !== 'branch') {
			return false;
		}

		return (edge.data as { switchNodeId?: string } | undefined)?.switchNodeId === node.id;
	});
}

function findLocalBehaviorSwitchForSourceStep(
	nodes: Node<BaseNodeData>[],
	edges: Edge[],
	sourceStepId: string | undefined
): BehaviorSwitchLike | null {
	if (!sourceStepId) {
		return null;
	}

	const sourceNodeId = `step-${sourceStepId}`;
	for (const node of nodes) {
		if (!isBehaviorSwitchNodeLinkedToSourceStep({ node, edges, sourceNodeId, sourceStepId })) {
			continue;
		}

		return (node.data as { automationStep?: BehaviorSwitchLike }).automationStep || null;
	}

	return null;
}

function analyzePlacementInTemplate(
	step: Api.IAutomationStep | null,
	template?: Api.IAutomationTemplate
): {
	hasNonBehaviorDownstream: boolean;
	hasBehaviorSwitch: boolean;
} {
	const sourceStepId = step?.id;
	if (!sourceStepId) {
		return {
			hasNonBehaviorDownstream: false,
			hasBehaviorSwitch: false,
		};
	}

	const steps = getTemplateSteps(template);
	const stepIndex = steps.findIndex(candidate => candidate.id === sourceStepId);
	if (stepIndex < 0) {
		return {
			hasNonBehaviorDownstream: false,
			hasBehaviorSwitch: false,
		};
	}

	let hasBehaviorSwitch = false;
	for (let i = stepIndex + 1; i < steps.length; i++) {
		const candidate = steps[i] as BehaviorSwitchLike;
		if (isBehaviorSwitchModel(candidate) && (!candidate.sourceStepId || candidate.sourceStepId === sourceStepId)) {
			hasBehaviorSwitch = true;
			continue;
		}

		return {
			hasNonBehaviorDownstream: true,
			hasBehaviorSwitch,
		};
	}

	return {
		hasNonBehaviorDownstream: false,
		hasBehaviorSwitch,
	};
}

/**
 * Finds the email engagement case definition from the available cases list
 * and checks whether a given engagement type predicate is satisfied.
 * The UI should only expose behavior options that the API explicitly advertises.
 */
function hasEmailEngagementSupport(
	availableCases: AvailableBehaviorCase[] | undefined,
	engagementPredicate: (normalizedType: string) => boolean
): boolean {
	if (!availableCases || availableCases.length === 0) {
		return false;
	}

	const emailCase = availableCases.find(
		candidate => (candidate.type || '').toLowerCase() === 'emailengagementcasestatement'
	);
	if (!emailCase) {
		return false;
	}

	if (!emailCase.availableEngagementTypes || emailCase.availableEngagementTypes.length === 0) {
		return true;
	}

	return emailCase.availableEngagementTypes.some(type => engagementPredicate((type || '').toLowerCase()));
}

function hasEmailReplySupport(availableCases: AvailableBehaviorCase[] | undefined): boolean {
	return hasEmailEngagementSupport(availableCases, type => type === 'reply');
}

function hasEmailOpenSupport(availableCases: AvailableBehaviorCase[] | undefined): boolean {
	return hasEmailEngagementSupport(availableCases, type => type === 'open');
}

function hasEmailClickSupport(availableCases: AvailableBehaviorCase[] | undefined): boolean {
	return hasEmailEngagementSupport(availableCases, type => type === 'anylinkclick' || type === 'specificlinkclick');
}

function createReplyCasePayload(_step: Api.IAutomationStep | null): BehaviorCaseStatementPayload {
	return {
		_type: 'EmailEngagementCaseStatement',
		name: 'If recipient replies',
		engagementType: 'Reply',
	};
}

function createOpenCasePayload(): BehaviorCaseStatementPayload {
	return {
		_type: 'EmailEngagementCaseStatement',
		name: 'If recipient opens',
		engagementType: 'Open',
	};
}

function createClickCasePayload(): BehaviorCaseStatementPayload {
	return {
		_type: 'EmailEngagementCaseStatement',
		name: 'If recipient clicks any links',
		engagementType: 'AnyLinkClick',
	};
}

function createTimeoutCasePayload(name: string): BehaviorCaseStatementPayload {
	return {
		_type: 'TimeoutCaseStatement',
		name,
	};
}

function isBehaviorCaseRetryableError(error: unknown): boolean {
	const normalizedMessage = (
		(error as { systemMessage?: string; message?: string })?.systemMessage ||
		(error as { message?: string })?.message ||
		''
	).toLowerCase();

	return (
		normalizedMessage.includes('not found') ||
		normalizedMessage.includes('does not exist') ||
		normalizedMessage.includes('404') ||
		normalizedMessage.includes('invalid step')
	);
}

async function retryBehaviorSyncMutation<T>(operation: () => Promise<T>, maxAttempts = 3): Promise<T> {
	for (let attempt = 1; attempt <= maxAttempts; attempt++) {
		try {
			return await operation();
		} catch (error) {
			if (attempt >= maxAttempts || !isBehaviorCaseRetryableError(error)) {
				throw error;
			}
			await new Promise(resolve => setTimeout(resolve, 120 * attempt));
		}
	}

	throw new Error('Behavior sync retry loop exited unexpectedly.');
}

export function useBehaviorConditionalOptions({
	nodeId,
	step,
	template,
	readonly,
	parentCaseInfo,
	impersonationContext,
}: UseBehaviorConditionalOptionsParams): UseBehaviorConditionalOptionsResult {
	const templateId = template?.id;
	const sourceStepId = step?.id;
	const queryClient = useQueryClient();
	const { automationTemplate: rootAutomationTemplate } = useAutomationFlowContext();
	const { getNode, getNodes, getEdges, setNodes, setEdges } = useReactFlow<Node<BaseNodeData>, Edge>();
	const canvasNodes = useNodes<Node<BaseNodeData>>();
	const canvasEdges = useEdges<Edge>();

	const addBehaviorSwitchStepMutation = useAddBehaviorSwitchStepMutation();
	const updateBehaviorSwitchStepMutation = useUpdateBehaviorSwitchStepMutation();
	const addBehaviorCaseMutation = useAddBehaviorCaseMutation();
	const updateBehaviorCaseMutation = useUpdateBehaviorCaseMutation();
	const removeBehaviorCaseMutation = useRemoveBehaviorCaseMutation();
	const removeStepMutation = useAutomationTemplateRemoveStepMutation();

	const { data: availableBehaviorCases } = useAvailableBehaviorCasesQuery({
		id: templateId || '',
		enabled: !!templateId,
		impersonationContext,
	});

	const templateSteps = getTemplateSteps(template);
	const localBehaviorSwitch = findLocalBehaviorSwitchForSourceStep(canvasNodes, canvasEdges, sourceStepId);
	const behaviorSwitch = localBehaviorSwitch ?? findBehaviorSwitchForSourceStep(templateSteps, sourceStepId);

	const derivedState = useMemo(() => {
		const cases = behaviorSwitch?.cases || [];
		const hasReplyCase = cases.some(caseStatement => isReplyBehaviorCase(caseStatement));
		const hasOpenCase = cases.some(caseStatement => isOpenBehaviorCase(caseStatement));
		const hasClickCase = cases.some(caseStatement => isClickBehaviorCase(caseStatement));
		const derivedWaitTimeoutDays = parseTimeSpanDays(behaviorSwitch?.waitTimeout);

		return {
			takeActionOnReply: hasReplyCase,
			takeActionOnOpen: hasOpenCase,
			takeActionOnClick: hasClickCase,
			waitTimeoutDays: derivedWaitTimeoutDays,
		};
	}, [behaviorSwitch?.cases, behaviorSwitch?.waitTimeout]);

	const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
	const [isSyncingBehaviorState, setIsSyncingBehaviorState] = useState(false);
	const [pendingState, setPendingState] = useState<BehaviorGraphState | null>(null);
	const behaviorSwitchSnapshotRef = useRef<BehaviorSwitchLike | null>(behaviorSwitch);

	useEffect(() => {
		if (isSyncingBehaviorState || pendingState) {
			return;
		}

		behaviorSwitchSnapshotRef.current = behaviorSwitch;
	}, [behaviorSwitch, isSyncingBehaviorState, pendingState]);

	// Front-end support is intentionally limited to email behavior branches for now.
	// Text and event behavior cases may exist in API models, but we should not expose
	// configuration UI for them until the full flow is implemented end-to-end.
	const canShowReplyOption =
		isEmailLikeStep(step) && (derivedState.takeActionOnReply || hasEmailReplySupport(availableBehaviorCases));
	const canShowOpenOption =
		isEmailLikeStep(step) && (derivedState.takeActionOnOpen || hasEmailOpenSupport(availableBehaviorCases));
	const canShowClickOption =
		isEmailLikeStep(step) && (derivedState.takeActionOnClick || hasEmailClickSupport(availableBehaviorCases));

	const placementAnalysis = useMemo(() => analyzePlacementInTemplate(step, template), [step, template]);

	// Reactive canvas-based check: detect non-behavior downstream edges in
	// real time so the disabled state updates immediately when segments are
	// added or removed, without waiting for a template refetch.
	// Subscribe via the public React Flow hooks so this stays compatible with
	// library upgrades and reacts immediately to node/edge updates.
	const hasCanvasDownstream = useMemo(() => {
		if (!nodeId) {
			return false;
		}

		const findCanvasNode = (nextNodeId: string) =>
			canvasNodes.find(node => node.id === nextNodeId) || getNode(nextNodeId);

		for (const edge of canvasEdges) {
			if (edge.source !== nodeId) {
				continue;
			}
			if (edge.type === 'phantomAddPath') {
				continue;
			}
			if (edge.type === 'branch') {
				const switchNodeId = (edge.data as { switchNodeId?: string } | undefined)?.switchNodeId;
				if (switchNodeId) {
					const switchNode = findCanvasNode(switchNodeId);
					if (
						switchNode &&
						isAutomationStepNodeData(switchNode.data) &&
						isBehaviorSwitchModel(switchNode.data.automationStep)
					) {
						continue;
					}
				}
			}

			const targetNode = findCanvasNode(edge.target);
			if (targetNode && targetNode.type !== 'phantomTarget') {
				return true;
			}
		}

		return false;
	}, [canvasEdges, canvasNodes, getNode, nodeId]);
	const currentBranchDepth = useMemo(() => getBranchNestingDepth(parentCaseInfo), [parentCaseInfo]);

	const hasBehaviorSwitch = !!behaviorSwitch?.id || placementAnalysis.hasBehaviorSwitch;

	useEffect(() => {
		if (!pendingState) {
			return;
		}

		// Don't clear pending state while a debounce timer or sync cycle is
		// active. The optimistic graph update causes derivedState (via
		// localBehaviorSwitch) to match pendingState immediately, but we
		// must wait for the server to actually persist the change before
		// treating it as caught up — otherwise the snapshot ref gets
		// contaminated with optimistic data and the sync is skipped.
		if (syncDebounceTimerRef.current || isSyncingBehaviorState) {
			return;
		}

		const hasCaughtUp =
			pendingState.nextReply === derivedState.takeActionOnReply &&
			pendingState.nextOpen === derivedState.takeActionOnOpen &&
			pendingState.nextClick === derivedState.takeActionOnClick &&
			pendingState.nextTimeoutDays === derivedState.waitTimeoutDays;

		if (hasCaughtUp) {
			setPendingState(null);
		}
	}, [
		pendingState,
		derivedState.takeActionOnReply,
		derivedState.takeActionOnOpen,
		derivedState.takeActionOnClick,
		derivedState.waitTimeoutDays,
		isSyncingBehaviorState,
	]);

	const displayedState = pendingState
		? {
				takeActionOnReply: pendingState.nextReply,
				takeActionOnOpen: pendingState.nextOpen,
				takeActionOnClick: pendingState.nextClick,
				waitTimeoutDays: pendingState.nextTimeoutDays,
			}
		: derivedState;

	const isBusy =
		isSyncingBehaviorState ||
		!!addBehaviorSwitchStepMutation?.isPending ||
		!!updateBehaviorSwitchStepMutation?.isPending ||
		!!addBehaviorCaseMutation?.isPending ||
		!!updateBehaviorCaseMutation?.isPending ||
		!!removeBehaviorCaseMutation?.isPending ||
		!!removeStepMutation?.isPending;

	// The canvas edge state is the live source of truth for downstream steps.
	// The template-based analysis can be stale after optimistic deletions.
	const disableForPlacement = hasCanvasDownstream && !hasBehaviorSwitch;
	const disableForMaxDepth = currentBranchDepth >= MAX_BRANCH_NESTING_DEPTH && !hasBehaviorSwitch;
	// Keep checkboxes interactive during sync so queued updates work.
	// The isBusy flag is still exposed as isUpdating for the "Saving" pill.
	const isDisabled = readonly || disableForPlacement || disableForMaxDepth;
	let disabledReason: string | null = null;
	if (readonly) {
		disabledReason = 'This automation is read-only.';
	} else if (disableForPlacement) {
		disabledReason =
			'Conditional actions can only be added to the last step before the end of a path. To enable, move this step to the end, or remove the steps after it.';
	} else if (disableForMaxDepth) {
		disabledReason = `Conditional actions can only be added up to ${MAX_BRANCH_NESTING_DEPTH} nested branches deep.`;
	}

	const showTimeoutEditor =
		displayedState.takeActionOnReply || displayedState.takeActionOnOpen || displayedState.takeActionOnClick;
	const timeoutCaseLabel = TIMEOUT_CASE_LABEL;
	const rootTemplateId = rootAutomationTemplate?.id;

	const refreshRootFlowSnapshot = useCallback(() => {
		if (!rootTemplateId || rootTemplateId === templateId) {
			return;
		}

		queryClient.setQueryData(AUTOMATION_QUERY_KEYS.template(rootTemplateId), previous => {
			if (!previous) {
				return previous;
			}
			return { ...(previous as Api.IAutomationTemplate) };
		});
		queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(rootTemplateId) });
	}, [queryClient, rootTemplateId, templateId]);

	const getLocalBehaviorSwitchNodeId = useCallback(
		(nextSourceStepId: string): string | undefined => {
			const currentNodes = getNodes() as Node<BaseNodeData>[];
			const currentEdges = getEdges() as Edge[];
			const sourceNodeId = `step-${nextSourceStepId}`;
			const localSwitchNode = currentNodes.find(node =>
				isBehaviorSwitchNodeLinkedToSourceStep({
					node,
					edges: currentEdges,
					sourceNodeId,
					sourceStepId: nextSourceStepId,
				})
			);
			return localSwitchNode?.id;
		},
		[getEdges, getNodes]
	);

	const reconcileOptimisticBehaviorSwitch = useCallback(
		(optimisticSwitchNodeId: string, persistedSwitchStep: BehaviorSwitchStepPayload) => {
			const currentNodes = getNodes() as Node<BaseNodeData>[];
			const currentEdges = getEdges() as Edge[];
			if (!currentNodes.some(node => node.id === optimisticSwitchNodeId)) {
				return;
			}

			const caseAssignments = buildPersistedBehaviorCaseAssignments({
				nodes: currentNodes,
				optimisticSwitchNodeId,
				persistedSwitchStep,
			});
			const syncedNodes = syncOptimisticBehaviorNodes({
				nodes: currentNodes,
				optimisticSwitchNodeId,
				persistedSwitchStep,
				automationTemplate: template,
				caseAssignments,
			});
			const syncedEdges = syncOptimisticBehaviorEdges({
				edges: currentEdges,
				optimisticSwitchNodeId,
				caseAssignments,
			});

			setNodes(syncedNodes);
			setEdges(syncedEdges);
		},
		[getEdges, getNodes, setEdges, setNodes, template]
	);

	const updatePersistedBehaviorSwitchSnapshot = useCallback((persistedSwitchStep: BehaviorSwitchStepPayload) => {
		behaviorSwitchSnapshotRef.current = persistedSwitchStep as BehaviorSwitchLike;
	}, []);

	const followUpRelayoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const syncDebounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		return () => {
			if (followUpRelayoutTimerRef.current) {
				clearTimeout(followUpRelayoutTimerRef.current);
				followUpRelayoutTimerRef.current = null;
			}
			if (syncDebounceTimerRef.current) {
				clearTimeout(syncDebounceTimerRef.current);
				syncDebounceTimerRef.current = null;
			}
		};
	}, []);

	const scheduleFollowUpOptimisticRelayout = useCallback(
		(nextState: BehaviorGraphState) => {
			if (followUpRelayoutTimerRef.current) {
				clearTimeout(followUpRelayoutTimerRef.current);
			}

			followUpRelayoutTimerRef.current = setTimeout(() => {
				followUpRelayoutTimerRef.current = null;
				const currentNodes = getNodes() as Node<BaseNodeData>[];
				const currentEdges = getEdges() as Edge[];
				const optimisticGraph = applyOptimisticBehaviorGraphUpdate({
					nodes: currentNodes,
					edges: currentEdges,
					step,
					template,
					parentCaseInfo,
					sourceStepId,
					nextState,
					getSourceStepName,
					createReplyCasePayload,
					createOpenCasePayload,
					createClickCasePayload,
					createTimeoutCasePayload,
				});

				setNodes(optimisticGraph.nodes);
				setEdges(optimisticGraph.edges);
			}, 120);
		},
		[getEdges, getNodes, parentCaseInfo, setEdges, setNodes, sourceStepId, step, template]
	);

	const ensureCanEdit = () => {
		if (!isDisabled) {
			return true;
		}

		setFeedbackMessage(
			disabledReason ||
				'Conditional actions can only be added to the last step before the end of a path. Move this step to the end, or remove the steps after it.'
		);
		return false;
	};

	const syncBehaviorState = async (
		nextState: BehaviorGraphState
	): Promise<{ optimisticSwitchNodeId: string; persistedSwitchStep: BehaviorSwitchStepPayload } | null> => {
		if (!templateId || !sourceStepId) {
			throw new Error('Missing template or step context for behavior update.');
		}

		const nextTimeout = formatTimeSpanDays(nextState.nextTimeoutDays);
		const currentSwitch = behaviorSwitchSnapshotRef.current;
		const localSwitchNodeId = getLocalBehaviorSwitchNodeId(sourceStepId) || `step-temp-behavior-switch-${sourceStepId}`;

		const createBehaviorSwitchFromScratch = async () => {
			const createdStep = await addBehaviorSwitchStepMutation.mutateAsync({
				templateId,
				step: {
					_type: 'BehaviorSwitchAutomationStep',
					name: 'Conditional Behavior',
					sourceStepId,
					waitTimeout: nextTimeout,
				},
				impersonationContext,
			});
			let createdPersistedSwitchStep = createdStep as BehaviorSwitchStepPayload;
			updatePersistedBehaviorSwitchSnapshot(createdPersistedSwitchStep);

			const createdStepId = createdStep.id;
			if (!createdStepId) {
				throw new Error('BehaviorSwitch step was created but returned no ID.');
			}

			if (nextState.nextReply) {
				createdPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
					addBehaviorCaseMutation.mutateAsync({
						templateId,
						stepId: createdStepId,
						caseData: createReplyCasePayload(step),
						impersonationContext,
					})
				);
				updatePersistedBehaviorSwitchSnapshot(createdPersistedSwitchStep);
			}
			if (nextState.nextOpen) {
				createdPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
					addBehaviorCaseMutation.mutateAsync({
						templateId,
						stepId: createdStepId,
						caseData: createOpenCasePayload(),
						impersonationContext,
					})
				);
				updatePersistedBehaviorSwitchSnapshot(createdPersistedSwitchStep);
			}
			if (nextState.nextClick) {
				createdPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
					addBehaviorCaseMutation.mutateAsync({
						templateId,
						stepId: createdStepId,
						caseData: createClickCasePayload(),
						impersonationContext,
					})
				);
				updatePersistedBehaviorSwitchSnapshot(createdPersistedSwitchStep);
			}
			createdPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				addBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: createdStepId,
					caseData: createTimeoutCasePayload(TIMEOUT_CASE_LABEL),
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(createdPersistedSwitchStep);

			return {
				optimisticSwitchNodeId: localSwitchNodeId,
				persistedSwitchStep: createdPersistedSwitchStep,
			};
		};

		if (!nextState.nextReply && !nextState.nextOpen && !nextState.nextClick) {
			if (currentSwitch?.id) {
				try {
					await retryBehaviorSyncMutation(() =>
						removeStepMutation.mutateAsync({
							templateId,
							stepType: 'BehaviorSwitch' as Api.AutomationStepType,
							stepId: currentSwitch.id,
							impersonationContext,
						})
					);
				} catch (error) {
					if (!isBehaviorCaseRetryableError(error)) {
						throw error;
					}
				}
			}
			behaviorSwitchSnapshotRef.current = null;
			return null;
		}

		if (!currentSwitch?.id) {
			return createBehaviorSwitchFromScratch();
		}

		let latestPersistedSwitchStep = currentSwitch as BehaviorSwitchStepPayload;
		const shouldUpdateWaitTimeout = parseTimeSpanDays(currentSwitch.waitTimeout) !== nextState.nextTimeoutDays;
		const shouldRepairSourceStepId = !hasUsableSourceStepId(currentSwitch.sourceStepId);
		if (shouldUpdateWaitTimeout || shouldRepairSourceStepId) {
			try {
				latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
					updateBehaviorSwitchStepMutation.mutateAsync({
						templateId,
						stepId: currentSwitch.id!,
						step: buildBehaviorSwitchStepUpdatePayload({
							behaviorSwitch: currentSwitch,
							sourceStepId,
							waitTimeout: nextTimeout,
						}),
						impersonationContext,
					})
				);
				updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
			} catch (error) {
				if (!isBehaviorCaseRetryableError(error)) {
					throw error;
				}
				behaviorSwitchSnapshotRef.current = null;
				return createBehaviorSwitchFromScratch();
			}
		}

		const cases = latestPersistedSwitchStep.cases || currentSwitch.cases || [];
		const replyCase = cases.find(caseStatement => isReplyBehaviorCase(caseStatement));
		const openCase = cases.find(caseStatement => isOpenBehaviorCase(caseStatement));
		const clickCase = cases.find(caseStatement => isClickBehaviorCase(caseStatement));
		const timeoutCase = cases.find(caseStatement => isTimeoutBehaviorCase(caseStatement));

		if (nextState.nextReply && !replyCase) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				addBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseData: createReplyCasePayload(step),
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (!nextState.nextReply && replyCase?.id) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				removeBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseId: replyCase.id,
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (nextState.nextOpen && !openCase) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				addBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseData: createOpenCasePayload(),
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (!nextState.nextOpen && openCase?.id) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				removeBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseId: openCase.id,
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (nextState.nextClick && !clickCase) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				addBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseData: createClickCasePayload(),
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (!nextState.nextClick && clickCase?.id) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				removeBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseId: clickCase.id,
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		if (!timeoutCase) {
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				addBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseData: createTimeoutCasePayload(TIMEOUT_CASE_LABEL),
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		} else if (timeoutCase.id && timeoutCase.name !== TIMEOUT_CASE_LABEL) {
			const normalizedTimeoutCase = timeoutCase as BehaviorCaseLike;
			latestPersistedSwitchStep = await retryBehaviorSyncMutation(() =>
				updateBehaviorCaseMutation.mutateAsync({
					templateId,
					stepId: currentSwitch.id!,
					caseId: timeoutCase.id,
					caseData: {
						_type: (normalizedTimeoutCase._type ||
							normalizedTimeoutCase.caseType ||
							'TimeoutCaseStatement') as BehaviorCaseStatementPayload['_type'],
						name: TIMEOUT_CASE_LABEL,
					},
					impersonationContext,
				})
			);
			updatePersistedBehaviorSwitchSnapshot(latestPersistedSwitchStep);
		}

		return {
			optimisticSwitchNodeId: localSwitchNodeId,
			persistedSwitchStep: latestPersistedSwitchStep,
		};
	};

	// Last-write-wins queue: rapid toggles are applied optimistically
	// immediately, but server sync is lightly debounced so quick click bursts
	// collapse into a single persistence cycle.
	const syncLockRef = useRef(false);
	const queuedUpdateRef = useRef<{
		nextState: BehaviorGraphState;
		errorMessage: string;
	} | null>(null);
	const pristineSnapshotRef = useRef<{ nodes: Node<BaseNodeData>[]; edges: Edge[] } | null>(null);

	// Keep a stable ref to syncBehaviorState so startSyncCycle doesn't need to
	// list it in its dependency array (the function recreates every render).
	const syncBehaviorStateRef = useRef(syncBehaviorState);
	syncBehaviorStateRef.current = syncBehaviorState;

	// Keep a stable ref to the latest applyOptimisticUpdate so queued drains
	// always read fresh closure state.
	const applyOptimisticUpdateRef = useRef<((nextState: BehaviorGraphState, errorMessage: string) => void) | null>(null);

	const startSyncCycle = useCallback(
		(nextState: BehaviorGraphState, errorMessage: string) => {
			syncLockRef.current = true;
			setIsSyncingBehaviorState(true);

			syncBehaviorStateRef
				.current(nextState)
				.then(syncResult => {
					const queued = queuedUpdateRef.current;

					if (!queued) {
						if (syncResult?.persistedSwitchStep) {
							reconcileOptimisticBehaviorSwitch(syncResult.optimisticSwitchNodeId, syncResult.persistedSwitchStep);
						}
						refreshRootFlowSnapshot();
					}

					// Drain the queue: if another toggle arrived during the sync,
					// dispatch it now with the latest closure via setTimeout(0).
					if (queued) {
						queuedUpdateRef.current = null;
						syncLockRef.current = false;
						setIsSyncingBehaviorState(false);
						setTimeout(() => {
							applyOptimisticUpdateRef.current?.(queued.nextState, queued.errorMessage);
						}, 0);
					}
				})
				.catch((error: unknown) => {
					// On error, discard any queued update and revert to the
					// pristine snapshot captured before the first optimistic change.
					queuedUpdateRef.current = null;
					setPendingState(null);
					const pristine = pristineSnapshotRef.current;
					if (pristine) {
						setNodes(pristine.nodes);
						setEdges(pristine.edges);
					}
					setFeedbackMessage((error as { systemMessage?: string })?.systemMessage || errorMessage);
				})
				.finally(() => {
					// Release the lock only if no queued drain is pending.
					// When a queued update exists, the .then() branch already
					// released the lock and dispatched the next cycle.
					if (!queuedUpdateRef.current) {
						syncLockRef.current = false;
						setIsSyncingBehaviorState(false);
						pristineSnapshotRef.current = null;
					}
				});
		},
		[
			reconcileOptimisticBehaviorSwitch,
			refreshRootFlowSnapshot,
			setNodes,
			setEdges,
			setIsSyncingBehaviorState,
			setPendingState,
		]
	);

	const applyOptimisticUpdate = useCallback(
		(nextState: BehaviorGraphState, errorMessage: string) => {
			if (followUpRelayoutTimerRef.current) {
				clearTimeout(followUpRelayoutTimerRef.current);
				followUpRelayoutTimerRef.current = null;
			}

			const isActivatingFirstBehavior =
				!displayedState.takeActionOnReply &&
				!displayedState.takeActionOnOpen &&
				!displayedState.takeActionOnClick &&
				(nextState.nextReply || nextState.nextOpen || nextState.nextClick);

			// Always apply the optimistic graph update immediately so the
			// canvas reflects the user's intent without waiting for the server.
			const currentNodes = getNodes() as Node<BaseNodeData>[];
			const currentEdges = getEdges() as Edge[];
			const optimisticGraph = applyOptimisticBehaviorGraphUpdate({
				nodes: currentNodes,
				edges: currentEdges,
				step,
				template,
				parentCaseInfo,
				sourceStepId,
				nextState,
				getSourceStepName,
				createReplyCasePayload,
				createOpenCasePayload,
				createClickCasePayload,
				createTimeoutCasePayload,
			});

			if (!syncLockRef.current && !syncDebounceTimerRef.current && !pristineSnapshotRef.current) {
				pristineSnapshotRef.current = { nodes: currentNodes, edges: currentEdges };
			}

			setPendingState(nextState);
			setFeedbackMessage(null);
			setNodes(optimisticGraph.nodes);
			setEdges(optimisticGraph.edges);

			if (isActivatingFirstBehavior) {
				scheduleFollowUpOptimisticRelayout(nextState);
			}

			if (syncLockRef.current) {
				// A sync is already in flight — queue this update (last-write-wins).
				queuedUpdateRef.current = { nextState, errorMessage };
				return;
			}

			if (syncDebounceTimerRef.current) {
				clearTimeout(syncDebounceTimerRef.current);
			}

			syncDebounceTimerRef.current = setTimeout(() => {
				syncDebounceTimerRef.current = null;

				if (doesBehaviorStateMatchSwitch(nextState, behaviorSwitchSnapshotRef.current, sourceStepId)) {
					setPendingState(null);
					pristineSnapshotRef.current = null;
					return;
				}

				startSyncCycle(nextState, errorMessage);
			}, BEHAVIOR_SYNC_DEBOUNCE_MS);
		},
		[
			displayedState.takeActionOnClick,
			displayedState.takeActionOnOpen,
			displayedState.takeActionOnReply,
			step,
			template,
			parentCaseInfo,
			sourceStepId,
			getNodes,
			getEdges,
			setNodes,
			setEdges,
			scheduleFollowUpOptimisticRelayout,
			startSyncCycle,
		]
	);

	// Keep the ref in sync so queued drains always call the latest closure.
	applyOptimisticUpdateRef.current = applyOptimisticUpdate;

	const onToggleReply = (checked: boolean) => {
		if (!ensureCanEdit()) {
			return;
		}

		applyOptimisticUpdate(
			{
				nextReply: checked,
				nextOpen: displayedState.takeActionOnOpen,
				nextClick: displayedState.takeActionOnClick,
				nextTimeoutDays: displayedState.waitTimeoutDays,
			},
			'Failed to update behavior options.'
		);
	};

	const onToggleOpen = (checked: boolean) => {
		if (!ensureCanEdit()) {
			return;
		}

		applyOptimisticUpdate(
			{
				nextReply: displayedState.takeActionOnReply,
				nextOpen: checked,
				nextClick: displayedState.takeActionOnClick,
				nextTimeoutDays: displayedState.waitTimeoutDays,
			},
			'Failed to update behavior options.'
		);
	};

	const onToggleClick = (checked: boolean) => {
		if (!ensureCanEdit()) {
			return;
		}

		applyOptimisticUpdate(
			{
				nextReply: displayedState.takeActionOnReply,
				nextOpen: displayedState.takeActionOnOpen,
				nextClick: checked,
				nextTimeoutDays: displayedState.waitTimeoutDays,
			},
			'Failed to update behavior options.'
		);
	};

	const onChangeTimeoutDays = (nextValue: number) => {
		if (!ensureCanEdit()) {
			return;
		}

		const safeValue = Math.max(1, Math.min(365, Number.isFinite(nextValue) ? Math.round(nextValue) : 1));
		applyOptimisticUpdate(
			{
				nextReply: displayedState.takeActionOnReply,
				nextOpen: displayedState.takeActionOnOpen,
				nextClick: displayedState.takeActionOnClick,
				nextTimeoutDays: safeValue,
			},
			'Failed to update timeout.'
		);
	};

	return {
		canShowReplyOption,
		canShowOpenOption,
		canShowClickOption,
		takeActionOnReply: displayedState.takeActionOnReply,
		takeActionOnOpen: displayedState.takeActionOnOpen,
		takeActionOnClick: displayedState.takeActionOnClick,
		waitTimeoutDays: displayedState.waitTimeoutDays,
		showTimeoutEditor,
		isDisabled,
		isUpdating: isBusy,
		disabledReason,
		feedbackMessage,
		timeoutCaseLabel,
		onToggleReply,
		onToggleOpen,
		onToggleClick,
		onChangeTimeoutDays,
	};
}
